/*
  Cart drawer extras (loaded by sections/cart-drawer.liquid)
  - <cart-gift-option>: "This is a gift" checkbox + gift note, saved to the cart
  - <cart-upsell>: suggested add-ons with one-tap Add buttons
  - <cart-gift-note>: gift note sheet opened from "This is a gift" in the drawer footer
*/
(() => {
  if (window.CartDrawerExtrasLoaded) return;
  window.CartDrawerExtrasLoaded = true;

  const routes = (window.FoxTheme && window.FoxTheme.routes) || {};
  const CART_URL = routes.cart_url || '/cart';
  const CART_ADD_URL = routes.cart_add_url || '/cart/add';
  const CART_UPDATE_URL = routes.cart_update_url || '/cart/update';

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  /* Ask the theme which sections (the cart drawer) to re-render with the cart response */
  const getSectionsToRender = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    if (!sections.length) {
      const drawer = document.querySelector('cart-drawer[data-section-id]');
      if (drawer) sections.push(drawer.dataset.sectionId);
    }
    return sections;
  };

  /* Same update path the theme uses after adding to cart: redraws the drawer and the cart count */
  const publishCart = async (sections) => {
    const response = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    cart.sections = sections;
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    }
  };

  /* ---------- Gift option ---------- */

  // What the shopper chose during this visit. Survives drawer re-renders.
  const giftState = { gift: null, note: null };
  let saveQueue = Promise.resolve();

  const saveCart = (body) => {
    saveQueue = saveQueue
      .catch(() => {})
      .then(() => postJSON(`${CART_UPDATE_URL}.js`, body))
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
        return response;
      });
    return saveQueue;
  };

  class CartGiftOption extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.toggle = this.querySelector('[data-cart-gift-toggle]');
      this.details = this.querySelector('[data-cart-gift-details]');
      this.note = this.querySelector('[data-cart-gift-note]');
      this.status = this.querySelector('[data-cart-gift-status]');
      if (!this.toggle) return;

      if (giftState.gift !== null) this.toggle.checked = giftState.gift;
      if (giftState.note !== null && this.note) this.note.value = giftState.note;
      this.sync();

      this.toggle.addEventListener('change', () => this.onToggle());

      if (this.note) {
        this.note.addEventListener('input', () => {
          giftState.note = this.note.value;
          this.setStatus('');
          clearTimeout(this.noteTimer);
          this.noteTimer = setTimeout(() => this.saveNote(), 600);
        });
        this.note.addEventListener('change', () => {
          clearTimeout(this.noteTimer);
          this.saveNote();
        });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
      clearTimeout(this.statusTimer);
    }

    sync() {
      const isGift = this.toggle.checked;
      if (this.details) this.details.hidden = !isGift;
      if (this.note) this.note.disabled = !isGift;
    }

    onToggle() {
      const isGift = this.toggle.checked;
      giftState.gift = isGift;
      if (this.note) giftState.note = this.note.value;
      this.sync();

      const body = isGift
        ? { attributes: { Gift: 'Yes' }, note: this.note ? this.note.value : '' }
        : { attributes: { Gift: '' }, note: '' };

      saveCart(body).catch(() => this.setStatus("Couldn't save that. Please try again."));
    }

    saveNote() {
      if (!this.note || !this.toggle.checked) return;
      const value = this.note.value;
      if (value === this.lastSavedNote) return;
      this.lastSavedNote = value;

      saveCart({ note: value })
        .then(() => {
          if (value.trim()) this.setStatus('Gift note saved');
        })
        .catch(() => {
          this.lastSavedNote = undefined;
          this.setStatus("Couldn't save your note. Please try again.");
        });
    }

    setStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
      clearTimeout(this.statusTimer);
      if (text) {
        this.statusTimer = setTimeout(() => {
          this.status.textContent = '';
        }, 2500);
      }
    }
  }

  /* ---------- Gift note sheet: "This is a gift" in the cart drawer footer ---------- */

  class CartGiftNote extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.note = this.querySelector('[data-cart-gift-note]');
      this.status = this.querySelector('[data-cart-gift-status]');
      this.saveButton = this.querySelector('[data-cart-gift-save]');
      this.removeButton = this.querySelector('[data-cart-gift-remove]');

      if (this.saveButton) this.saveButton.addEventListener('click', () => this.save());
      if (this.removeButton) this.removeButton.addEventListener('click', () => this.remove());
      if (this.note) this.note.addEventListener('input', () => this.setStatus(''));
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
    }

    get trigger() {
      return document.getElementById(this.dataset.trigger);
    }

    get modal() {
      return this.closest('cart-addon-modal');
    }

    // Keeps the footer checkbox marked once the note is saved, so shoppers know we got it
    setChecked(isGift) {
      const trigger = this.trigger;
      if (trigger) {
        trigger.classList.toggle('is-checked', isGift);
        const label = trigger.querySelector('[data-cart-gift-saved-label]');
        if (label) label.textContent = isGift ? ', gift note saved' : '';
      }
      if (this.removeButton) this.removeButton.hidden = !isGift;
    }

    setStatus(text, isError = false) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle('alert--success', !isError);
      this.status.classList.toggle('alert--error', isError);
    }

    setBusy(button, busy) {
      if (!button) return;
      button.disabled = busy;
      if (busy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    closeSoon() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        const modal = this.modal;
        if (modal && typeof modal.hide === 'function') modal.hide();
      }, 1400);
    }

    async save() {
      if (this.busy) return;
      this.busy = true;
      this.setBusy(this.saveButton, true);
      const note = this.note ? this.note.value.trim() : '';

      try {
        await saveCart({ note, attributes: { Gift: 'Yes' } });
        giftState.gift = true;
        giftState.note = note;
        this.setChecked(true);
        this.setStatus(note ? 'Got it. Your gift note is saved.' : 'Got it. This order is marked as a gift.');
        this.closeSoon();
      } catch (error) {
        this.setStatus("Couldn't save that. Please try again.", true);
      } finally {
        this.busy = false;
        this.setBusy(this.saveButton, false);
      }
    }

    async remove() {
      if (this.busy) return;
      this.busy = true;
      this.setBusy(this.removeButton, true);

      try {
        await saveCart({ note: '', attributes: { Gift: '' } });
        giftState.gift = false;
        giftState.note = '';
        if (this.note) this.note.value = '';
        this.setChecked(false);
        this.setStatus('');
        const modal = this.modal;
        if (modal && typeof modal.hide === 'function') modal.hide();
      } catch (error) {
        this.setStatus("Couldn't update that. Please try again.", true);
      } finally {
        this.busy = false;
        this.setBusy(this.removeButton, false);
      }
    }
  }

  /* ---------- Suggested add-ons ---------- */

  // Last loaded suggestions per URL, so the box doesn't blink when the drawer redraws.
  const upsellCache = new Map();

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onVariantChange(event));

      const url = this.dataset.url;
      if (url && upsellCache.has(url)) this.render(upsellCache.get(url));
      this.load();
    }

    render(html) {
      if (!html || html.indexOf('data-cart-upsell-item') === -1) {
        this.hidden = true;
        return;
      }
      this.innerHTML = html;
      this.hidden = false;
    }

    async load() {
      const url = this.dataset.url;
      if (!url) return;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations failed (${response.status})`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('cart-upsell');
        const html = fresh ? fresh.innerHTML : '';
        upsellCache.set(url, html);
        if (this.isConnected) this.render(html);
      } catch (error) {
        if (!upsellCache.has(url)) this.hidden = true;
      }
    }

    onVariantChange(event) {
      const select = event.target.closest('[data-cart-upsell-variant]');
      if (!select) return;
      const option = select.options[select.selectedIndex];
      const row = select.closest('[data-cart-upsell-item]');
      const price = row && row.querySelector('[data-cart-upsell-price]');
      if (price && option && option.dataset.price) price.textContent = option.dataset.price;
    }

    async onClick(event) {
      const button = event.target.closest('[data-cart-upsell-add]');
      if (!button || button.disabled) return;
      event.preventDefault();

      const row = button.closest('[data-cart-upsell-item]');
      const select = row && row.querySelector('[data-cart-upsell-variant]');
      const error = row && row.querySelector('[data-cart-upsell-error]');
      const label = button.querySelector('[data-cart-upsell-add-label]');
      const variantId = Number(select ? select.value : button.dataset.variantId);
      if (!variantId) return;

      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding...';

      try {
        const sections = getSectionsToRender();
        const response = await postJSON(`${CART_ADD_URL}.js`, {
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || "Couldn't add this item.");
        }
        if (label) label.textContent = 'Added';
        await publishCart(data.sections);
      } catch (err) {
        if (error) {
          error.textContent = (err && err.message) || "Couldn't add this item.";
          error.hidden = false;
        }
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (label) label.textContent = 'Add';
      }
    }
  }

  if (!customElements.get('cart-gift-option')) customElements.define('cart-gift-option', CartGiftOption);
  if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
  if (!customElements.get('cart-gift-note')) customElements.define('cart-gift-note', CartGiftNote);
})();
